import { useEffect, useState } from 'react';
import { websocketBaseUrl } from 'App';
import { getTabEnvironmentHash } from 'services/authentication/environmentScope';

// Singleton manager for the single multiplexed updates websocket.
// One connection per tab carries all realtime updates, components
// listen to message types and subscribe to page bound channels.

// Type of the listener functions receiving every socket message
type SocketListener = (message: any) => void;

// Holds the current websocket connection
let webSocket: WebSocket | null = null;

// Tracks whether the socket should reconnect after a close
let shouldReconnect = false;

// Holds the pending reconnect timeout
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

// Reconnect delay bounds in milliseconds, doubled per failed attempt
const RECONNECT_BASE_DELAY = 10000;
const RECONNECT_RETRY_LATER_DELAY = 60000;
const RECONNECT_MAX_DELAY = 300000;

// Close code the server sends when it gave up because its backend was unavailable
const TRY_AGAIN_LATER_CODE = 1013;

// Delay of the next reconnect attempt, grows while the socket keeps failing
let reconnectDelay = RECONNECT_BASE_DELAY;

// Listener waiting for a hidden tab to become visible before reconnecting
let visibilityListener: (() => void) | null = null;

// Returns the next reconnect delay with jitter and doubles it for the attempt after
const nextReconnectDelay = (closeCode: number) => {
    if (closeCode === TRY_AGAIN_LATER_CODE) reconnectDelay = Math.max(reconnectDelay, RECONNECT_RETRY_LATER_DELAY);
    const delay = reconnectDelay * (1 + Math.random() * 0.5);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_DELAY);
    return delay;
};

// Removes the pending visibility listener
const clearVisibilityListener = () => {
    if (visibilityListener) {
        document.removeEventListener('visibilitychange', visibilityListener);
        visibilityListener = null;
    }
};

// Registered listeners receiving every incoming message
const listeners = new Set<SocketListener>();

// Reference counts per subscribed page bound channel
const channelCounts = new Map<string, number>();

// Sends a subscribe or unsubscribe action for a channel when the socket is open
const sendChannelAction = (action: 'subscribe' | 'unsubscribe', channel: string) => {
    if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        webSocket.send(JSON.stringify({ action, channel }));
    }
};

// Opens the websocket connection and keeps it alive with reconnects
export const connectUpdatesSocket = () => {
    // Skip when a connection is already open or connecting
    if (webSocket && (webSocket.readyState === WebSocket.OPEN || webSocket.readyState === WebSocket.CONNECTING)) return;

    shouldReconnect = true;

    // Configure the websocket url, a pinned tab joins the groups of its own environment
    const tabEnvironmentHash = getTabEnvironmentHash();
    const environmentQuery = tabEnvironmentHash ? `?environment=${encodeURIComponent(tabEnvironmentHash)}` : '';
    webSocket = new WebSocket(`${websocketBaseUrl}/updates/${environmentQuery}`);

    // Reset the backoff and resend the active channel subscriptions after every connect
    webSocket.onopen = () => {
        reconnectDelay = RECONNECT_BASE_DELAY;
        channelCounts.forEach((count, channel) => {
            if (count > 0) sendChannelAction('subscribe', channel);
        });
    };

    // Parse the received message and notify every listener
    webSocket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        listeners.forEach((listener) => listener(data));
    };

    // Reconnect after a growing delay so the tabs never hammer the server together
    webSocket.onclose = (event) => {
        if (shouldReconnect) {
            reconnectTimeout = setTimeout(reconnectWhenVisible, nextReconnectDelay(event.code));
        }
    };
};

// Reconnects now, or once a hidden tab becomes visible again so background tabs hold no connection
function reconnectWhenVisible() {
    reconnectTimeout = null;
    if (!shouldReconnect) return;

    // Connect right away when the tab is visible
    if (!document.hidden) {
        connectUpdatesSocket();
        return;
    }

    // Wait for the tab to become visible before connecting
    if (visibilityListener) return;
    visibilityListener = () => {
        if (document.hidden) return;
        clearVisibilityListener();
        if (shouldReconnect) connectUpdatesSocket();
    };
    document.addEventListener('visibilitychange', visibilityListener);
}

// Closes the websocket connection and stops reconnecting
export const disconnectUpdatesSocket = () => {
    shouldReconnect = false;

    // Clear a pending reconnect attempt
    if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
    }
    clearVisibilityListener();
    reconnectDelay = RECONNECT_BASE_DELAY;

    // Close the current connection
    if (webSocket) {
        webSocket.close();
        webSocket = null;
    }
};

// Hook returning the latest socket message matching one of the given types
export const useSocketMessage = (types: string[]): { message: any } => {
    const [message, setMessage] = useState<any>(null);

    useEffect(() => {
        // Register a listener storing messages of the wanted types
        const listener = (data: any) => {
            if (types.includes(data?.type)) setMessage(data);
        };
        listeners.add(listener);

        // Remove the listener on unmount
        return () => {
            listeners.delete(listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(types)]);

    return { message };
};

// Hook subscribing to a page bound channel and returning its latest message
export const useSocketChannel = (channel: string, types: string[]): { message: any } => {
    useEffect(() => {
        // Increase the reference count and subscribe on first use
        const count = channelCounts.get(channel) || 0;
        channelCounts.set(channel, count + 1);
        if (count === 0) sendChannelAction('subscribe', channel);

        // Decrease the reference count and unsubscribe on last unmount
        return () => {
            const current = channelCounts.get(channel) || 0;
            channelCounts.set(channel, Math.max(current - 1, 0));
            if (current <= 1) sendChannelAction('unsubscribe', channel);
        };
    }, [channel]);

    return useSocketMessage(types);
};
